import { config } from '@/config';
import { useAuth } from './auth';

export const useBlog = () => {
    const { getAuthHeaders } = useAuth();

    // Public API calls (no authentication required)
    const getPublicPosts = async (limit = 20, offset = 0) => {
        try {
            const response = await fetch(
                `${config.API_BASE_URL}/api/v1/posts?limit=${limit}&offset=${offset}`
            );
            if (response.ok) {
                return await response.json();
            }
            throw new Error('Failed to fetch posts');
        } catch (error) {
            console.error('Error fetching public posts:', error);
            throw error;
        }
    };

    const getPublicPost = async (slug) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/posts/${slug}`);
            if (response.ok) {
                return await response.json();
            }
            throw new Error('Post not found');
        } catch (error) {
            console.error('Error fetching public post:', error);
            throw error;
        }
    };

    // Admin API calls (authentication required)
    const getAllPosts = async (limit = 20, offset = 0) => {
        try {
            const response = await fetch(
                `${config.API_BASE_URL}/api/v1/admin/posts?limit=${limit}&offset=${offset}`,
                {
                    headers: getAuthHeaders(),
                }
            );
            if (response.ok) {
                return await response.json();
            }
            throw new Error('Failed to fetch admin posts');
        } catch (error) {
            console.error('Error fetching admin posts:', error);
            throw error;
        }
    };

    const getPost = async (id) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/posts/${id}`, {
                headers: getAuthHeaders(),
            });
            if (response.ok) {
                return await response.json();
            }
            throw new Error('Post not found');
        } catch (error) {
            console.error('Error fetching post:', error);
            throw error;
        }
    };

    const createPost = async (postData) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/posts`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(postData),
            });

            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create post');
            }
        } catch (error) {
            console.error('Error creating post:', error);
            throw error;
        }
    };

    const updatePost = async (id, postData) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/posts/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(postData),
            });

            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update post');
            }
        } catch (error) {
            console.error('Error updating post:', error);
            throw error;
        }
    };

    const deletePost = async (id) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/posts/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete post');
            }
        } catch (error) {
            console.error('Error deleting post:', error);
            throw error;
        }
    };

    const publishPost = async (id) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/posts/${id}/publish`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to publish post');
            }
        } catch (error) {
            console.error('Error publishing post:', error);
            throw error;
        }
    };

    const unpublishPost = async (id) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/v1/admin/posts/${id}/unpublish`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to unpublish post');
            }
        } catch (error) {
            console.error('Error unpublishing post:', error);
            throw error;
        }
    };

    return {
        // Public API
        getPublicPosts,
        getPublicPost,
        // Admin API
        getAllPosts,
        getPost,
        createPost,
        updatePost,
        deletePost,
        publishPost,
        unpublishPost,
    };
};